import { Loader2, MapPin, QrCode, Fingerprint, CheckCircle2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ValidationStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  status: 'pending' | 'loading' | 'complete';
}

interface ValidationLoaderProps {
  steps: ValidationStep[];
  title: string;
}

export const ValidationLoader = ({ steps, title }: ValidationLoaderProps) => {
  return (
    <div className="py-8 flex flex-col items-center gap-6">
      <div className="relative">
        <Skeleton className="h-20 w-20 rounded-full" />
        <Loader2 className="h-8 w-8 animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-primary" />
      </div>
      
      <div className="text-center">
        <h3 className="font-semibold text-lg mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">Proszę czekać...</p>
      </div>

      <div className="w-full max-w-xs space-y-3">
        {steps.map((step) => (
          <div 
            key={step.id}
            className={cn(
              "flex items-center gap-3 p-3 rounded-lg transition-all duration-300",
              step.status === 'complete' && "bg-green-50 dark:bg-green-950/30",
              step.status === 'loading' && "bg-primary/5",
              step.status === 'pending' && "opacity-50"
            )}
          >
            <div className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full",
              step.status === 'complete' && "bg-green-500 text-white",
              step.status === 'loading' && "bg-primary/20",
              step.status === 'pending' && "bg-muted"
            )}>
              {step.status === 'complete' ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : step.status === 'loading' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-muted-foreground">{step.icon}</span>
              )}
            </div>
            <span className={cn(
              "text-sm font-medium",
              step.status === 'complete' && "text-green-600 dark:text-green-400"
            )}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const getCheckInSteps = (currentStep: number): ValidationStep[] => [
  {
    id: 'gps',
    label: 'Weryfikacja lokalizacji GPS',
    icon: <MapPin className="h-4 w-4" />,
    status: currentStep > 0 ? 'complete' : currentStep === 0 ? 'loading' : 'pending',
  },
  {
    id: 'qr',
    label: 'Skanowanie kodu QR',
    icon: <QrCode className="h-4 w-4" />,
    status: currentStep > 1 ? 'complete' : currentStep === 1 ? 'loading' : 'pending',
  },
  {
    id: 'biometric',
    label: 'Weryfikacja biometryczna',
    icon: <Fingerprint className="h-4 w-4" />,
    status: currentStep > 2 ? 'complete' : currentStep === 2 ? 'loading' : 'pending',
  },
];

export const getCheckOutSteps = (currentStep: number): ValidationStep[] => [
  {
    id: 'gps',
    label: 'Potwierdzenie lokalizacji GPS',
    icon: <MapPin className="h-4 w-4" />,
    status: currentStep > 0 ? 'complete' : currentStep === 0 ? 'loading' : 'pending',
  },
  {
    id: 'qr',
    label: 'Skanowanie kodu QR',
    icon: <QrCode className="h-4 w-4" />,
    status: currentStep > 1 ? 'complete' : currentStep === 1 ? 'loading' : 'pending',
  },
  {
    id: 'biometric',
    label: 'Weryfikacja biometryczna',
    icon: <Fingerprint className="h-4 w-4" />,
    status: currentStep > 2 ? 'complete' : currentStep === 2 ? 'loading' : 'pending',
  },
];
